import { type ReactElement, useMemo, useState } from 'react';
import { ListChecks } from 'lucide-react';
import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { releases } from '@kirin/api-client/contracts';
import { flattenInstallerChecksums } from './installerHashes';

interface InstallerHashDisplayProps {
  /**
   * SHA-256 of the file the download button points at (the current platform).
   * Optional: absent when the current download isn't in the manifest (version
   * skew, or an unpublished/gated platform). The "View all checksums" popout
   * stays reachable regardless, so other OSes' hashes are never stranded.
   */
  sha256?: string;
  /**
   * Filename of the current download (e.g. `Kirin-3.84.1.pkg`), shown as
   * `filename : digest` so it's clear which installer — name and extension —
   * the hash belongs to. Also names the copy control for accessibility.
   */
  filename?: string;
  /**
   * The full published manifest — flattened into the "View all checksums"
   * popout so an admin can allow-list every OS's installer (macOS / Windows /
   * Linux, every arch and version) from one place, whatever OS they're on.
   */
  hashes: releases.InstallerHashes;
}

/**
 * The digest itself: clipped with an ellipsis when it doesn't fit but kept whole
 * in the DOM (so it stays selectable, and the copy button always yields the full
 * value); the full digest is also in the title tooltip.
 */
function Digest({
  sha256,
  copyLabel,
}: {
  sha256: string;
  copyLabel: string;
}): ReactElement {
  return (
    <>
      <span className="font-mono truncate min-w-0" title={sha256}>
        {sha256}
      </span>
      <CopyToClipboardButton
        text={sha256}
        variant="ghost"
        size="icon"
        compactCopiedState
        className="h-5 w-5 shrink-0 text-muted-foreground"
      >
        <span className="sr-only">{copyLabel}</span>
      </CopyToClipboardButton>
    </>
  );
}

/**
 * KIR-4071: shows the SHA-256 of the current-platform installer beneath its
 * download button, so a customer can allow-list the exact file in their EDR.
 * Everything sits on one line — the "SHA-256 (EDR allow-list)" label, the
 * installer filename, `filename : digest`, copy, and a "View all checksums"
 * button that pops out every published installer's digest (all OSes, arches, and
 * versions), so an admin allow-listing from a different OS than they deploy to
 * (a Mac admin rolling Kirin to a Windows fleet) gets every hash from one place.
 *
 * When the current download has no published digest (version skew, or an
 * unpublished/gated platform), the per-file line is dropped but the "View all
 * checksums" popout still renders on its own — the aggregate hashes must never
 * be stranded behind one platform's match. Renders nothing only when there is
 * neither a current digest nor any checksums to browse.
 */
export function InstallerHashDisplay({
  sha256,
  filename,
  hashes,
}: InstallerHashDisplayProps): ReactElement | null {
  const [query, setQuery] = useState('');
  const rows = useMemo(() => flattenInstallerChecksums(hashes), [hashes]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      r =>
        r.file.toLowerCase().includes(q) ||
        r.version.toLowerCase().includes(q) ||
        r.sha256.includes(q)
    );
  }, [rows, query]);

  // Group the (already newest-first) rows by version so an admin reads all of
  // one release's OS installers together — the way an EDR allow-list is built.
  const groups = useMemo(() => {
    const out: { version: string; rows: typeof filtered }[] = [];
    for (const row of filtered) {
      const last = out[out.length - 1];
      if (last && last.version === row.version) last.rows.push(row);
      else out.push({ version: row.version, rows: [row] });
    }
    return out;
  }, [filtered]);

  const rowClass =
    'flex items-center gap-1.5 min-w-0 text-[11px] leading-5 text-muted-foreground';

  // Nothing to render: no digest for the current file AND no checksums to browse.
  if (!sha256 && rows.length === 0) return null;

  // Show the popout whenever there is more to see than the current file alone —
  // and, when the current file itself has no published digest, whenever any
  // checksums exist at all (so other OSes' hashes stay reachable on skew/gating).
  const showPopout = sha256 ? rows.length > 1 : rows.length > 0;

  return (
    <div className="mt-3">
      <div className={rowClass}>
        {sha256 ? (
          <>
            <span className="shrink-0">SHA-256 (EDR allow-list)</span>
            {filename && (
              <span className="shrink-0 whitespace-nowrap font-mono font-medium text-foreground">
                {filename} :
              </span>
            )}
            <Digest
              sha256={sha256}
              copyLabel={
                filename ? `Copy SHA-256 for ${filename}` : 'Copy SHA-256'
              }
            />
          </>
        ) : (
          <span className="shrink-0">Installer checksums (SHA-256)</span>
        )}
        {showPopout && (
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 shrink-0 gap-1 px-1.5 text-[11px] text-muted-foreground hover:text-foreground"
              >
                <ListChecks className="h-3 w-3" aria-hidden />
                View all checksums ({rows.length})
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl">
              <DialogHeader>
                <DialogTitle>Installer checksums (SHA-256)</DialogTitle>
                <DialogDescription>
                  Every published installer and its digest — macOS, Windows, and
                  Linux, grouped by version — for EDR allow-listing. Search by
                  filename, version, or hash.
                </DialogDescription>
              </DialogHeader>
              <Input
                placeholder="Filter checksums…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="font-mono"
              />
              <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-3">
                {groups.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    No checksums match “{query}”.
                  </p>
                ) : (
                  groups.map(group => (
                    <section
                      key={group.version}
                      className="overflow-hidden rounded-md border"
                    >
                      <h4 className="sticky top-0 z-10 flex items-baseline gap-2 border-b bg-muted px-3 py-2">
                        <span className="font-mono text-sm font-semibold text-foreground">
                          v{group.version}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {group.rows.length}{' '}
                          {group.rows.length === 1 ? 'installer' : 'installers'}
                        </span>
                      </h4>
                      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                        {group.rows.map(row => (
                          <li
                            key={`${row.version}:${row.file}`}
                            className="px-3 py-2.5"
                          >
                            <div className="truncate font-mono text-sm font-medium">
                              {row.file}
                            </div>
                            <div className="mt-0.5 flex items-center gap-2">
                              <span
                                className="min-w-0 flex-1 truncate font-mono text-[11px] leading-5 text-muted-foreground"
                                title={row.sha256}
                              >
                                {row.sha256}
                              </span>
                              <CopyToClipboardButton
                                text={row.sha256}
                                variant="ghost"
                                size="icon"
                                compactCopiedState
                                className="h-7 w-7 shrink-0 text-muted-foreground"
                              >
                                <span className="sr-only">
                                  Copy SHA-256 for {row.file}
                                </span>
                              </CopyToClipboardButton>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ))
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}

InstallerHashDisplay.displayName = 'InstallerHashDisplay';
